import React, { useState, useEffect, useContext } from 'react';

import get from 'lodash/get';
import map from 'lodash/map';
import find from 'lodash/find';
import join from 'lodash/join';
import noop from 'lodash/noop';

import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';

import { DataContext } from '../data/DataContext';
import { formatHistoryDate } from '../data/formatting';
import { DataContextType } from '../data_types/interfaces';
import { PlainObject, NormalizedGameData } from '../data_types/types';
import { placeNumber } from '../data/player_insights';
import DataTableToolbarWithExportAndSearch from '../shared/DataTableToolbarWithExportAndSearch';
import MatchCardModal from '../../components/shared/MatchCardModal';
import { DS, Panel, numericSx, trendColor, formatSigned } from '../shared/Dashboard';

const positionColor = (position: string) => {
    switch (placeNumber(position)) {
        case 1: return DS.gold;
        case 2: return DS.silver;
        case 3: return DS.bronze;
        case 4: return DS.textMuted;
        default: return DS.loss;
    }
};

/* ─── column definitions ──────────────────────────────────── */
const columnsDataGrid: GridColDef[] = [
    {
        field: 'matchNum',
        headerName: '#',
        headerClassName: 'rh-header',
        width: 60,
        renderCell: ({ value }) => (
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: DS.textFaint, ...numericSx }}>
                {value}
            </Typography>
        ),
    },
    {
        field: 'date',
        headerName: 'Date',
        headerClassName: 'rh-header',
        flex: 1,
        minWidth: 160,
        valueFormatter: (val) => formatHistoryDate(val),
        renderCell: ({ formattedValue }) => (
            <Typography sx={{ fontSize: '13px', color: DS.textMuted, ...numericSx }}>{formattedValue}</Typography>
        ),
    },
    {
        field: 'position',
        headerName: 'Position',
        headerClassName: 'rh-header',
        width: 100,
        renderCell: ({ value }) => {
            const color = positionColor(value);
            return (
                <Box sx={{
                    px: 1.25, py: 0.4,
                    borderRadius: '8px',
                    background: alpha(color, 0.14),
                    border: `1px solid ${alpha(color, 0.35)}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    minWidth: 36,
                    justifyContent: 'center',
                }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 800, color }}>
                        {value}
                    </Typography>
                </Box>
            );
        },
    },
    {
        field: 'ratingChange',
        headerName: 'Rating Δ',
        headerClassName: 'rh-header',
        width: 110,
        renderCell: ({ value }) => {
            const color = trendColor(value);
            const Icon = value > 0 ? ArrowUpwardIcon : value < 0 ? ArrowDownwardIcon : RemoveIcon;
            return (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color }}>
                    <Icon sx={{ fontSize: 13 }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, ...numericSx }}>
                        {formatSigned(value)}
                    </Typography>
                </Box>
            );
        },
    },
    {
        field: 'currentRating',
        headerName: 'Rating',
        headerClassName: 'rh-header',
        width: 90,
        renderCell: ({ value }) => (
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: DS.text, ...numericSx }}>{value}</Typography>
        ),
    },
    {
        field: 'seasonPoints',
        headerName: 'Pts',
        headerClassName: 'rh-header',
        width: 70,
        renderCell: ({ value }) => (
            <Typography sx={{ fontSize: '13px', color: DS.textMuted, ...numericSx }}>{value}</Typography>
        ),
    },
    {
        field: 'opponents',
        headerName: 'Opponents',
        headerClassName: 'rh-header',
        disableExport: true,
        valueGetter: (val, row) => join(map(get(row, 'card.playerData'), 'name'), ' '),
        minWidth: 100,
    },
];

/* ─── main component ──────────────────────────────────────── */
const MatchHistoryTable = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { historyData, matchData, isClubMember } = context || {};

    const [rows, setRows] = useState<NormalizedGameData[]>([]);
    const [selectedMatch, setSelectedMatch] = useState<PlainObject | null>(null);

    useEffect(() => {
        if (historyData) {
            setRows(map(historyData, (gameData: NormalizedGameData) => ({
                ...gameData,
                id: gameData.matchNum,
                card: find(matchData, ['timestamp', gameData.date]),
            })));
        }
    }, [historyData, matchData]);

    return (
        <>
            <Panel
                title="Round history"
                subtitle={`Every round this season${isClubMember ? ' · click a row to open the scorecard' : ''}`}
                delay={0.05}
                noPadding
            >
                <Box sx={{
                    height: 520,
                    width: '100%',
                    borderTop: `1px solid ${DS.border}`,
                    '& .rh-header': {
                        color: DS.textMuted,
                        fontSize: '11px !important',
                        fontWeight: '700 !important',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                    },
                    '& .MuiDataGrid-root': { border: 'none', background: 'transparent' },
                    '& .MuiDataGrid-cell': { borderColor: DS.border, display: 'flex', alignItems: 'center' },
                    '& .MuiDataGrid-row': {
                        borderLeft: '3px solid transparent',
                        '&:hover': {
                            background: `${alpha(DS.brand, 0.06)} !important`,
                            cursor: isClubMember ? 'pointer' : 'default',
                        },
                    },
                    '& .row-win':  { borderLeftColor: alpha(DS.gain, 0.6) },
                    '& .row-loss': { borderLeftColor: alpha(DS.loss, 0.5) },
                    '& .row-even': { borderLeftColor: alpha(DS.neutral, 0.35) },
                    '& .MuiDataGrid-footerContainer': { borderColor: DS.border },
                    '& .MuiDataGrid-toolbarContainer': {
                        px: 2, pt: 1.5, pb: 1,
                        borderBottom: `1px solid ${DS.border}`,
                    },
                    '& .MuiDataGrid-container--top [role="row"]': {
                        background: `${DS.surface} !important`,
                    },
                    '& .MuiTablePagination-root': { color: DS.textMuted },
                }}>
                    <DataGrid
                        rows={rows}
                        columns={columnsDataGrid}
                        autosizeOnMount
                        autosizeOptions={{ includeOutliers: true, includeHeaders: true }}
                        density="compact"
                        rowHeight={44}
                        getRowClassName={({ row }) =>
                            row.ratingChange > 0 ? 'row-win'
                            : row.ratingChange < 0 ? 'row-loss'
                            : 'row-even'
                        }
                        initialState={{
                            // Initial (uncontrolled) so "Manage columns" can toggle columns
                            columns: { columnVisibilityModel: { opponents: false } },
                            pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
                            filter: { filterModel: { items: [], quickFilterExcludeHiddenColumns: false } },
                        }}
                        slots={{ toolbar: DataTableToolbarWithExportAndSearch }}
                        sx={{ border: 0 }}
                        onRowClick={
                            isClubMember
                                ? (params) => { setSelectedMatch(params.row); }
                                : noop
                        }
                    />
                </Box>
            </Panel>

            <MatchCardModal
                matchData={selectedMatch}
                isOpen={!!selectedMatch}
                onClose={() => { setSelectedMatch(null); }}
            />
        </>
    );
};

export default MatchHistoryTable;
